/**
 * Bruecke zwischen der offenen Buehne und dem Knopf „Ansicht ▾" in der Kopfleiste
 * (Rafael, 03.10.2026: „das Umschalten zwischen den Modi ist für einen normalen Nutzer
 * nicht nachvollziehbar" — die fuenf Umschalter oben stehen jetzt in EINEM Menue).
 *
 * Die Wahl Dokument / Reiter / Frei gehoert der Buehne (BuehnenArbeitsflaeche haelt
 * Zustand und Speicherung). Sie meldet hier, was gerade gilt, und wie man es aendert;
 * das Menue liest es. Ist kein Kopfmenue angemeldet (eigene Vorschauseite, Panel ohne
 * Kopfleiste), zeigt die Buehne ihre drei Knoepfe weiter selbst — die Funktion geht
 * nirgends verloren.
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { BuehnenModus } from '../../shared/buehnen-anordnung';

export interface BuehnenAnsicht {
  modus: BuehnenModus;
  hatDokument: boolean;
  waehlen: (m: BuehnenModus) => void;
}

const gemeldet = new Map<symbol, BuehnenAnsicht>();
let aktuell: BuehnenAnsicht | null = null;
let kopfMenues = 0;
const hoerer = new Set<() => void>();
const melden = () => { for (const h of hoerer) h(); };
const abo = (h: () => void) => { hoerer.add(h); return () => { hoerer.delete(h); }; };

/** Von der Buehne: aktuellen Stand melden (`null` = Buehne geht weg). */
export function buehnenAnsichtMelden(schluessel: symbol, stand: BuehnenAnsicht | null) {
  if (stand) gemeldet.set(schluessel, stand); else gemeldet.delete(schluessel);
  const alle = [...gemeldet.values()];
  aktuell = alle.length ? alle[alle.length - 1] : null;
  melden();
}

/** Fuer das Kopfmenue: was die offene Buehne gerade zeigt (oder `null`). Meldet das Menue an. */
export function useBuehnenAnsicht(): BuehnenAnsicht | null {
  useEffect(() => {
    kopfMenues++; melden();
    return () => { kopfMenues--; melden(); };
  }, []);
  return useSyncExternalStore(abo, () => aktuell, () => null);
}

/** Fuer die Buehne: steht ein Kopfmenue bereit, das die Wahl anbietet? */
export function useKopfBietetBuehnenWahl(): boolean {
  return useSyncExternalStore(abo, () => kopfMenues > 0, () => false);
}

export const BUEHNEN_MODUS_TEXT: Record<BuehnenModus, { kurz: string; knopf: string; erklaerung: string }> = {
  dokument: { kurz: 'Dokument', knopf: 'Nur das Dokument', erklaerung: 'Nur die Seite der Bühne, so groß wie möglich.' },
  tabs: { kurz: 'Reiter', knopf: 'Als Reiter', erklaerung: 'Alle Fenster als Reiter hintereinander, das Dokument vorne. Oben tippst du, welches du sehen willst.' },
  buehne: { kurz: 'Frei', knopf: 'Frei nebeneinander', erklaerung: 'Die Fenster stehen nebeneinander; du kannst sie ziehen und in der Größe ändern.' },
};
